import React from 'react';
import {EmprestimoItemAPI} from '../../../../services/creditoMova';
import {LoanDTO} from '../../../../dtos/EmprestimoDTO';
import {LoanWarning} from '../../../../context/EmprestimoAlerts';
import {formatDateBR, formatCentavos} from '../../../../utils/format';

import {
  LoanCard,
  LoanWarningBanner,
  LoanWarningIcon,
  LoanWarningText,
  LoanHeader,
  LoanHeaderTitle,
  LoanHeaderDate,
  LoanContent,
  LoanStatusPill,
  LoanStatusText,
  LoanInfoBlock,
  LoanInfoLabel,
  LoanInfoRow,
  LoanInfoValue,
  LoanCardAction,
  LoanCardActionText,
  LoanCardActionIcon,
} from './styles';

type Props = {
  item: EmprestimoItemAPI;
  warning: LoanWarning | null;
  onVerParcelas: () => void;
};

function getStatusText(status: string): string {
  switch (status) {
    case 'liquidada':
      return 'Ativo';
    case 'em_liquidacao':
      return 'Em andamento';
    case 'quitada':
      return 'Quitado';
    case 'contrato_confirmado':
      return 'Contrato confirmado';
    default:
      return status;
  }
}

function getStatusVariant(status: string): LoanDTO['status'] {
  switch (status) {
    case 'quitada':
      return 'quitado';
    case 'liquidada':
      return 'aprovado';
    case 'em_liquidacao':
      return 'analise';
    default:
      return 'aprovado';
  }
}

export function EmprestimoItemCard({item, warning, onVerParcelas}: Props) {
  const statusVariant = getStatusVariant(item.status);
  const statusText = getStatusText(item.status);
  const isActive = item.status !== 'quitada';

  return (
    <LoanCard warning={warning?.status}>
      {warning && (
        <LoanWarningBanner warning={warning.status}>
          <LoanWarningIcon
            warning={warning.status}
            name={warning.status === 'em_atraso' ? 'alert-circle' : 'clock'}
          />
          <LoanWarningText warning={warning.status}>
            {warning.status === 'em_atraso'
              ? `Parcela em atraso há ${warning.diasAtraso} ${warning.diasAtraso === 1 ? 'dia' : 'dias'}`
              : warning.status === 'vence_hoje'
              ? 'Parcela vence hoje'
              : 'Parcela próxima do vencimento'}
          </LoanWarningText>
        </LoanWarningBanner>
      )}

      <LoanHeader activeOpacity={1} onPress={() => {}}>
        <LoanHeaderTitle>
          Contratado em{' '}
          <LoanHeaderDate>
            {item.data_assinatura
              ? formatDateBR(item.data_assinatura)
              : formatDateBR(item.data_aprovacao)}
          </LoanHeaderDate>
        </LoanHeaderTitle>
        <LoanStatusPill variant={statusVariant}>
          <LoanStatusText variant={statusVariant}>{statusText}</LoanStatusText>
        </LoanStatusPill>
      </LoanHeader>

      <LoanContent>
        <LoanInfoBlock>
          <LoanInfoRow>
            <LoanInfoLabel>Valor contratado:</LoanInfoLabel>
            <LoanInfoValue>
              {formatCentavos(item.valor_contratado_centavos)}
            </LoanInfoValue>
          </LoanInfoRow>
          <LoanInfoRow>
            <LoanInfoLabel>Valor desembolsado:</LoanInfoLabel>
            <LoanInfoValue>
              {formatCentavos(item.valor_desembolsado_centavos)}
            </LoanInfoValue>
          </LoanInfoRow>
          <LoanInfoRow>
            <LoanInfoLabel>Parcelas:</LoanInfoLabel>
            <LoanInfoValue>{item.prazo_meses}x</LoanInfoValue>
          </LoanInfoRow>
          {item.encargos_total_centavos > 0 && (
            <LoanInfoRow>
              <LoanInfoLabel>Encargos:</LoanInfoLabel>
              <LoanInfoValue>
                {formatCentavos(item.encargos_total_centavos)}
              </LoanInfoValue>
            </LoanInfoRow>
          )}
          {item.data_desembolso && (
            <LoanInfoRow>
              <LoanInfoLabel>Desembolso em:</LoanInfoLabel>
              <LoanInfoValue>{formatDateBR(item.data_desembolso)}</LoanInfoValue>
            </LoanInfoRow>
          )}
          {item.data_quitacao && (
            <LoanInfoRow>
              <LoanInfoLabel>Quitado em:</LoanInfoLabel>
              <LoanInfoValue>{formatDateBR(item.data_quitacao)}</LoanInfoValue>
            </LoanInfoRow>
          )}
        </LoanInfoBlock>

        {isActive && (
          <LoanCardAction onPress={onVerParcelas}>
            <LoanCardActionText>Ver parcelas</LoanCardActionText>
            <LoanCardActionIcon name="arrow-right" />
          </LoanCardAction>
        )}
      </LoanContent>
    </LoanCard>
  );
}
